import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1519335664-9434143e2067?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8YmxhY2tfYW5kX3doaXRlfDE3ODA4MDQ1MTN8MA&ixlib=rb-4.1.0&q=85";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-24 md:pt-36 pb-14 md:pb-24 section-gutter overflow-hidden"
    >
      {/* Top meta row */}
      <motion.div
        initial="hidden"
        animate="show"
        variants={fade}
        custom={0}
        className="flex flex-wrap items-center justify-between gap-4 mb-12 md:mb-16"
      >
        <div className="flex items-center gap-3">
          <span className="tag-pill" data-testid="hero-tag">
            <span className="w-1.5 h-1.5 bg-black rounded-full" />
            Narzis Ventures LLP · Est. 2021 · Kochi
          </span>
        </div>
        <div className="hidden md:flex items-center gap-6 text-[0.72rem] uppercase tracking-[0.2em] font-semibold text-black/70">
          <span>Kerala · Gulf · London · Singapore</span>
          <span>—</span>
          <span>The Perception Quarterly</span>
        </div>
      </motion.div>

      <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
        {/* Headline */}
        <div className="col-span-12 lg:col-span-8">
          <motion.h1
            initial="hidden"
            animate="show"
            variants={fade}
            custom={1}
            className="hero-headline text-[14vw] sm:text-[12vw] md:text-[11vw] lg:text-[9.2vw]"
            data-testid="hero-headline"
          >
            The Power
            <br />
            of <em className="italic font-normal">Perception.</em>
          </motion.h1>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fade}
            custom={3}
            className="mt-8 md:mt-12 grid grid-cols-12 gap-6 items-start"
          >
            <div className="col-span-12 md:col-span-7">
              <p className="text-base md:text-lg leading-relaxed text-black/70 max-w-xl">
                A full-service <em>Corporate Communications</em> and
                <em> Integrated Marketing</em> house — advising premium brands,
                educational institutions, financial institutions, listed
                corporates and global logistics operators across
                <em> India, the Gulf and beyond.</em>
              </p>
            </div>
            <div className="col-span-12 md:col-span-5 flex md:justify-end items-center gap-4">
              <button
                onClick={() => scrollTo("contact")}
                data-testid="hero-cta-primary"
                className="btn-invert px-6 py-4 text-[0.72rem] uppercase tracking-[0.22em] font-bold inline-flex items-center gap-2"
              >
                Brief Us <ArrowUpRight size={16} />
              </button>
              <button
                onClick={() => scrollTo("services")}
                data-testid="hero-cta-secondary"
                className="btn-outline px-6 py-4 text-[0.72rem] uppercase tracking-[0.22em] font-bold"
              >
                Our Practice
              </button>
            </div>
          </motion.div>
        </div>

        {/* Image column */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={fade}
          custom={2}
          className="col-span-12 lg:col-span-4 relative"
          data-testid="hero-image-block"
        >
          <div className="relative aspect-[3/4] w-full border border-black overflow-hidden">
            <img
              src={HERO_IMG}
              alt="Editorial corporate architecture"
              className="w-full h-full object-cover bw"
              loading="eager"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-black p-4">
              <div className="overline text-black/70">Index No.</div>
              <div className="font-serif-display text-3xl">04 · 100%</div>
              <div className="text-[0.7rem] uppercase tracking-[0.18em] text-black/60 mt-1">
                Years · Success Rate
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="mt-16 flex items-center justify-between">
        <div className="flex items-center gap-3 text-[0.7rem] uppercase tracking-[0.22em] text-black/60">
          <ArrowDown size={14} /> Scroll · Read the Brief
        </div>
        <div className="hidden md:block font-serif-display italic text-black/40 text-xl">
          “Narrative is policy. Policy is power.”
        </div>
      </div>
    </section>
  );
};

export default Hero;
